import {
  LOGIN_ACCENT,
  LOGIN_ACCENT_DIM,
  MOBILE_SHELL_BASE_COLOR,
} from '../../components/auth/loginDarkTheme';
import { buildMobileInputZoomFixOverrides, MOBILE_INPUT_FONT_SIZE } from './mobileInputZoomFix';

const GLASS_SURFACE =
  'linear-gradient(155deg, rgba(0,0,0,0.72) 0%, rgba(3,7,18,0.88) 100%)';
const BORDER = 'rgba(255, 255, 255, 0.09)';
const INPUT_BG = 'rgba(15, 23, 42, 0.65)';

/** Login-matching MUI overrides for mobile app shell (native + compact viewport). */
export function buildMobileShellDarkTheme(accentColor = LOGIN_ACCENT) {
  const inputZoomFix = buildMobileInputZoomFixOverrides();

  return {
    palette: {
      mode: 'dark',
      primary: {
        main: accentColor,
        light: '#5eead4',
        dark: '#14b8a6',
        contrastText: '#04120e',
      },
      background: {
        default: MOBILE_SHELL_BASE_COLOR,
        paper: 'rgba(3, 7, 18, 0.92)',
      },
      text: {
        primary: '#f8fafc',
        secondary: 'rgba(148, 163, 184, 0.92)',
      },
      divider: 'rgba(255, 255, 255, 0.08)',
    },
    components: {
      ...inputZoomFix,
      MuiCssBaseline: {
        styleOverrides: {
          ...(inputZoomFix.MuiCssBaseline?.styleOverrides || {}),
          body: {
            backgroundColor: 'var(--rn-mobile-shell-bg, ' + MOBILE_SHELL_BASE_COLOR + ')',
            color: 'var(--rn-mobile-text-primary, #f8fafc)',
          },
          '#root': {
            minHeight: '100vh',
          },
          '.capacitor-native #root, .capacitor-native body, html.capacitor-native': {
            minHeight: '100vh',
            height: '100%',
          },
          '#djDebug, #djDebugToolbar, #djdt-ToolbarHandle, .djdt-hidden': {
            display: 'none !important',
            visibility: 'hidden !important',
          },
          '.mobile-shell-theme, .mobile-shell-theme body, .mobile-app-shell, .mobile-app-shell main, .mobile-route-surface': {
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          },
          '.mobile-shell-theme::-webkit-scrollbar, .mobile-shell-theme body::-webkit-scrollbar, .mobile-app-shell::-webkit-scrollbar, .mobile-app-shell *::-webkit-scrollbar, .mobile-route-surface::-webkit-scrollbar': {
            display: 'none',
            width: 0,
            height: 0,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            '&:not([data-dp-surface="true"])': {
              backgroundImage: GLASS_SURFACE,
              backgroundColor: 'rgba(3, 7, 18, 0.88)',
              border: `1px solid ${BORDER}`,
              color: '#f8fafc',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            '&:not([data-dp-surface="true"])': {
              backgroundImage: GLASS_SURFACE,
              backgroundColor: 'rgba(3, 7, 18, 0.88)',
              border: `1px solid ${BORDER}`,
              color: '#f8fafc',
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(255, 255, 255, 0.08)',
            color: '#e2e8f0',
          },
          head: {
            color: '#94a3b8',
            backgroundColor: 'rgba(0, 0, 0, 0.35)',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:nth-of-type(even)': {
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
            },
            '&:hover': {
              backgroundColor: 'rgba(76, 225, 182, 0.06) !important',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          input: {
            fontSize: MOBILE_INPUT_FONT_SIZE,
          },
          root: {
            borderRadius: '14px',
            backgroundColor: INPUT_BG,
            color: '#f8fafc',
            fontSize: MOBILE_INPUT_FONT_SIZE,
            '& input': {
              fontSize: MOBILE_INPUT_FONT_SIZE,
            },
            '& fieldset': {
              borderColor: 'rgba(148, 163, 184, 0.28)',
            },
            '&:hover:not(.Mui-disabled):not(.Mui-focused) fieldset': {
              borderColor: LOGIN_ACCENT_DIM,
            },
            '&.Mui-focused fieldset': {
              borderColor: `${accentColor} !important`,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: 'rgba(148, 163, 184, 0.9)',
            '&.Mui-focused': {
              color: `${accentColor} !important`,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontSize: MOBILE_INPUT_FONT_SIZE,
          },
          icon: {
            color: 'rgba(148, 163, 184, 0.9)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            // Opaque — glass gradients let page content bleed through dropdowns.
            backgroundImage: 'none',
            backgroundColor: '#0b1220',
            border: `1px solid ${BORDER}`,
            color: '#f8fafc',
          },
          list: {
            color: '#f8fafc',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: '#0b1220',
            border: `1px solid ${BORDER}`,
            color: '#f8fafc',
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: '#f8fafc',
            '&:hover': {
              backgroundColor: 'rgba(76, 225, 182, 0.08)',
            },
            '&.Mui-selected': {
              backgroundColor: 'rgba(76, 225, 182, 0.12)',
              color: '#f8fafc',
              '&:hover': {
                backgroundColor: 'rgba(76, 225, 182, 0.16)',
              },
            },
            '&.Mui-disabled': {
              opacity: 1,
              color: 'rgba(148, 163, 184, 0.72)',
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            color: 'rgba(148, 163, 184, 0.92)',
            minWidth: 36,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            color: '#f8fafc',
          },
          secondary: {
            color: 'rgba(148, 163, 184, 0.92)',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderColor: 'rgba(255, 255, 255, 0.12)',
            color: '#e2e8f0',
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: '14px',
          },
          standardInfo: {
            backgroundColor: 'rgba(76, 225, 182, 0.1)',
            border: `1px solid ${LOGIN_ACCENT_DIM}`,
            color: '#d1fae5',
          },
          standardError: {
            backgroundColor: 'rgba(239, 68, 68, 0.12)',
            border: '1px solid rgba(239, 68, 68, 0.35)',
            color: '#fecaca',
          },
          standardWarning: {
            backgroundColor: 'rgba(251, 146, 60, 0.12)',
            border: '1px solid rgba(251, 146, 60, 0.35)',
            color: '#fed7aa',
          },
          standardSuccess: {
            backgroundColor: 'rgba(34, 197, 94, 0.12)',
            border: '1px solid rgba(34, 197, 94, 0.35)',
            color: '#bbf7d0',
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: accentColor,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            color: 'rgba(148, 163, 184, 0.9)',
            '&.Mui-selected': {
              color: accentColor,
            },
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: '#e2e8f0',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: 'inherit',
          },
        },
      },
    },
  };
}
